import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// MUI
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Badge from "@mui/material/Badge";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import Logout from "@mui/icons-material/Logout";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PaidIcon from "@mui/icons-material/Paid";

// Components
import NotificationItem from "./NotificationItem";
import homeLogo from "../../../Images/TPLLogo.png";
import BidCards from "./BidCards";

// Utils
import {
  getStorageValue,
  setStorageValue,
  showThousandSuperator,
} from "../../../utility/utils";

// Hooks
import {
  useAppSelector,
  useAppDispatch,
} from "../../../redux/hooks/redux-hooks";

// Actions
import { logOutUser } from "../../../redux/actions/UserActions";
import {
  fetchProjectNotifications,
  readAllProjectNotifications,
} from "../../../redux/actions/ProjectNotificationActions";
import { ComponentName } from "../../../SectionWiseClassification";

// Config
import { appConfig } from "../../../utility/config";

// MSAL
import { useMsal } from "@azure/msal-react";

type CountType = {
  count: number;
  value: number;
};

type ProjectStatusKey =
  | "savedAsDraft"
  | "bidPipeline"
  | "submitted"
  | "gonogoApproved"
  | "gonogoRejected"
  | "bidSubmitted"
  | "win"
  | "loss"
  | "bidUniverse";

type Props = {
  componentName: string;
  setCurrentComponent: React.Dispatch<React.SetStateAction<ComponentName>>;
  setBadgeClickValue: (val: string) => void;
  counts: {
    savedAsDraft: CountType;
    bidPipeline: CountType;
    submitted: CountType;
    gonogoApproved: CountType;
    gonogoRejected: CountType;
    bidSubmitted: CountType;
    win: CountType;
    loss: CountType;
    bidUniverse: CountType;
  };
  handleDrawerToggle: () => void;
};

const HeaderSection = ({
  componentName,
  setCurrentComponent,
  setBadgeClickValue,
  counts,
  handleDrawerToggle,
}: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { instance } = useMsal();

  const loggedInUser = useAppSelector((state) => state.user.loggedIn_User);
  const projectNotifications = useAppSelector(
    (state) => state.projectNotifications.notifications
  );

  // To get the active notifications count
  const activeNotificationsCount = useMemo(() => {
    return projectNotifications.filter((rec) => rec.isActive === true).length;
  }, [projectNotifications]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorUserMenu, setAnchorUserMenu] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  // To fetch the project notifications
  useEffect(() => {
    if (loggedInUser.userId) {
      dispatch(fetchProjectNotifications(loggedInUser.userId));
    }
  }, [loggedInUser, dispatch]);

  const handleMenuOpen = (event: any) => setAnchorUserMenu(event.currentTarget);
  const handleMenuClose = () => setAnchorUserMenu(null);

  const handleLogOut = async () => {
    handleMenuClose();
    dispatch(logOutUser());
    await instance.logoutRedirect();
    navigate("/login", { replace: true });
  };

  const handleDashboardClick = (selectedMenu: ComponentName) => {
    handleMenuClose();
    setCurrentComponent(selectedMenu);
  };

  const handleAdminClick = () => {
    const user = getStorageValue(appConfig.loginUserStorageKey);
    setStorageValue(appConfig.loginUserStorageKey, {
      ...user,
      roleId: loggedInUser.switchRoleId,
      switchRoleId: loggedInUser.roleId,
    });
    window.location.reload();
  };

  const handleNotificationsClick = () => {
    if (activeNotificationsCount !== 0) {
      dispatch(readAllProjectNotifications(loggedInUser.userId));
    }
  };

  // To show sidebar on click for mobile view
  const handleBidCardDrawerOpen = () => {
    handleMenuClose();
    setMobileOpen(true);
  };

  const handleBidCardDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleBadgeClick = (val: string) => {
    setCurrentComponent(ComponentName.BidDetails);
    setBadgeClickValue(val);
    handleBidCardDrawerClose();
  };

  // To get the count value of card
  const getCardCount = (key: ProjectStatusKey) => {
    if (!counts[key].count) {
      return "0 | 0";
    }
    let takeTotal = 0;
    if (key === "bidPipeline") {
      takeTotal = counts.bidPipeline.count;
    } else if (key === "gonogoApproved") {
      takeTotal = counts.submitted.count;
    } else if (key === "gonogoRejected") {
      takeTotal = counts.submitted.count;
    } else if (key === "bidSubmitted") {
      takeTotal = counts.gonogoApproved.count;
    } else if (key === "win") {
      takeTotal = counts.bidSubmitted.count;
    } else if (key === "loss") {
      takeTotal = counts.bidSubmitted.count;
    }

    const percentage = (counts[key].count / takeTotal) * 100;
    const roundedPercentage = Math.round(percentage); // Round to the nearest integer
    return `${counts[key].count} | ${roundedPercentage}%`;
  };

  // To get the price value of card
  const getCardPrice = (key: ProjectStatusKey) => {
    if (!counts[key].value) {
      return "0 | 0";
    }

    let takeTotal = 0;
    if (key === "bidPipeline") {
      takeTotal = counts.bidUniverse.value;
    } else if (key === "gonogoApproved") {
      takeTotal = counts.submitted.value;
    } else if (key === "gonogoRejected") {
      takeTotal = counts.submitted.value;
    } else if (key === "bidSubmitted") {
      takeTotal = counts.gonogoApproved.value;
    } else if (key === "win") {
      takeTotal = counts.bidSubmitted.value;
    } else if (key === "loss") {
      takeTotal = counts.bidSubmitted.value;
    }

    const percentage = Math.round((counts[key].value / takeTotal) * 100);
    return `${showThousandSuperator(counts[key].value)} | ${percentage}%`;
  };

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        backgroundColor: "#333399",
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar disableGutters sx={{ height: `${appConfig.headerHeight}px` }}>
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Stack direction="row" alignItems="center">
            <Box
              sx={{
                width: { xs: "100px", sm: "170px" },
                cursor: "pointer",
                backgroundColor: "#fff",
                borderRadius: "5px",
                padding: { xs: "1em 5px", md: "1em" },
                marginLeft: "0.5em",
              }}
              onClick={() => handleDashboardClick(ComponentName.Dashboard)}
            >
              <img src={homeLogo} alt="TataLogo" width="100%" />
            </Box>
            <Tooltip title="Home" arrow placement="bottom">
              <IconButton
                size="large"
                aria-label="home"
                sx={{
                  padding: 0,
                  marginLeft: { xs: "0.5em", md: "1em" },
                  marginRight: { xs: "0.5em", md: "1em" },
                  alignSelf: "center",
                }}
                onClick={() => handleDashboardClick(ComponentName.Dashboard)}
              >
                <HomeIcon sx={{ color: "#fff" }} fontSize="inherit" />
              </IconButton>
            </Tooltip>
            {loggedInUser.switchRoleId ? (
              <Button
                variant="contained"
                color="primary"
                sx={{
                  alignSelf: "center",
                  marginRight: { xs: "0.5em", md: "1em" },
                }}
                onClick={handleAdminClick}
              >
                Admin
              </Button>
            ) : null}
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            flexGrow={1}
            p={1}
            display={{ xs: "none", md: "flex" }}
          >
            <BidCards
              handleBadgeClick={handleBadgeClick}
              getCardCount={getCardCount}
              getCardPrice={getCardPrice}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="end">
            <Badge
              badgeContent={activeNotificationsCount}
              color="error"
              overlap="circular"
              onClick={(event) => {
                handleNotificationsClick();
                setAnchorEl(event.currentTarget);
              }}
              sx={{
                cursor: "pointer",
                mx: { xs: 1, md: 2 },
                alignSelf: "center",
              }}
            >
              <NotificationsNoneOutlinedIcon
                fontSize="large"
                sx={{ color: "#fff" }}
              />
            </Badge>
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
            >
              <Typography variant="h6" sx={{ padding: "0 0.5rem" }}>
                Notifications
              </Typography>
              <Divider />
              {projectNotifications.length ? (
                <List sx={{ padding: "0 0.5rem" }}>
                  {projectNotifications.map((notification, i) => (
                    <NotificationItem key={i} data={notification} />
                  ))}
                </List>
              ) : (
                <ListItem>
                  <ListItemText primary="No new notifications" />
                </ListItem>
              )}
            </Popover>
            <IconButton
              size="large"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <AccountCircleIcon sx={{ color: "#fff" }} fontSize="large" />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorUserMenu}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              open={Boolean(anchorUserMenu)}
              onClose={handleMenuClose}
            >
              <MenuItem>
                <ListItemIcon>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                {loggedInUser.fullName}
              </MenuItem>
              <Divider sx={{ backgroundColor: "#000", m: "0 0 !important" }} />
              <MenuItem
                onClick={() => handleDashboardClick(ComponentName.Dashboard)}
              >
                <ListItemIcon>
                  <HomeIcon fontSize="small" />
                </ListItemIcon>
                Home
              </MenuItem>
              <MenuItem
                sx={{ display: { xs: "flex", md: "none" } }}
                onClick={handleBidCardDrawerOpen}
              >
                <ListItemIcon>
                  <MenuIcon fontSize="small" />
                </ListItemIcon>
                Bid Cards
              </MenuItem>
              <MenuItem
                onClick={() => handleDashboardClick(ComponentName.WinLoss)}
              >
                <ListItemIcon>
                  <PaidIcon fontSize="small" />
                </ListItemIcon>
                Win Loss
              </MenuItem>
              <MenuItem
                onClick={() => handleDashboardClick(ComponentName.BidDetails)}
              >
                <ListItemIcon>
                  <GroupWorkIcon fontSize="small" />
                </ListItemIcon>
                Pipeline Pulse
              </MenuItem>
              <MenuItem
                onClick={() =>
                  handleDashboardClick(ComponentName.ProjectReport)
                }
              >
                <ListItemIcon>
                  <ListAltIcon fontSize="small" />
                </ListItemIcon>
                Project Report
              </MenuItem>
              <MenuItem onClick={handleLogOut}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Sign Out
              </MenuItem>
            </Menu>
            <Drawer
              anchor="right"
              variant="temporary"
              open={mobileOpen}
              onClose={handleBidCardDrawerClose}
              ModalProps={{
                keepMounted: true,
              }}
              sx={{
                display: { xs: "block", md: "none" },
                "& .MuiDrawer-paper": {
                  boxSizing: "border-box",
                  width: "240px",
                },
              }}
            >
              <div>
                <Toolbar sx={{ height: `${appConfig.headerHeight}px` }} />
                <Stack direction="column" spacing={1} p={1}>
                  <BidCards
                    handleBadgeClick={handleBadgeClick}
                    getCardCount={getCardCount}
                    getCardPrice={getCardPrice}
                  />
                </Stack>
              </div>
            </Drawer>
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default HeaderSection;
